/**
 * My Brands Tab - Manage your brands
 */
import { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react'
import { useAuth } from '@trafiflow/shared-ui'
import { Button } from '@trafiflow/shared-ui'
import { Plus, Edit, Trash2, Tag, RefreshCw, Building2 } from 'lucide-react'
import apiClient from '../../../../shared/lib/api'
import type { Brand } from '../../types'
import BrandModal from '../../components/BrandModal'
import BrandEntityView from '../../components/BrandEntityView'

export interface MyBrandsTabRef {
  refresh: () => void
}

const MyBrandsTab = forwardRef<MyBrandsTabRef>((_props, ref) => {
  const { user } = useAuth()
  const [brands, setBrands] = useState<Brand[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [viewingEntityBrand, setViewingEntityBrand] = useState<Brand | null>(null)

  const fetchBrands = useCallback(async () => {
    if (!user?.tenantId) return

    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/brands')
      setBrands(response.data.brands || [])
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { message?: string } } }
      if (error.response?.status === 404) {
        setBrands([])
        setError(null)
      } else {
        console.error('Error fetching brands:', err)
        setError(error.response?.data?.message || 'Failed to load brands')
        setBrands([])
      }
    } finally {
      setLoading(false)
    }
  }, [user?.tenantId])

  useEffect(() => {
    fetchBrands()
  }, [fetchBrands])

  useImperativeHandle(ref, () => ({
    refresh: fetchBrands
  }))

  const handleCreate = () => {
    setEditingBrand(null)
    setIsModalOpen(true)
  }

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this brand? This will also delete all associated stores.')) {
      return
    }

    try {
      await apiClient.delete(`/brands/${id}`)
      await fetchBrands()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } }
      console.error('Error deleting brand:', err)
      alert(error.response?.data?.message || 'Failed to delete brand')
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingBrand(null)
  }

  const handleModalSave = async () => {
    await fetchBrands()
    handleModalClose()
  }

  const handleRefreshEntityIDs = async () => {
    if (!user?.tenantId) return

    try {
      setIsRefreshing(true)
      setError(null)
      await apiClient.post('/brands/fix-entity-ids')
      
      // Recargar la grilla
      await fetchBrands()
    } catch (err: unknown) {
      console.error('Error fixing entity IDs:', err)
      // Silently handle error - no user notification
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleViewEntity = (brand: Brand) => {
    setViewingEntityBrand(brand)
  }

  const handleBackToList = () => {
    setViewingEntityBrand(null)
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-10 h-10 border-2 border-tf-border border-t-primary rounded-full animate-spin mb-4" />
        <p className="text-sm font-medium text-tf-text-sub">Loading brands...</p>
      </div>
    )
  }

  // Show entity view if a brand is selected
  if (viewingEntityBrand) {
    return <BrandEntityView brand={viewingEntityBrand} onBack={handleBackToList} />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-tf-text">Brands</h2>
          <p className="text-sm text-tf-text-muted mt-0.5">
            {brands.length} {brands.length === 1 ? 'brand' : 'brands'} registered
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefreshEntityIDs}
            disabled={isRefreshing}
            className="p-2.5 text-tf-text-muted hover:text-tf-text-sub hover:bg-tf-bg-hover rounded-lg transition-colors disabled:opacity-50"
            title="Refresh Entity IDs"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <Button
            variant="primary"
            onClick={handleCreate}
            icon={<Plus className="w-4 h-4" />}
          >
            Add Brand
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-tf-feedback-error-bg border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {brands.length === 0 ? (
        <div className="text-center py-16 rounded-xl border-2 border-dashed border-tf-border bg-tf-bg-muted">
          <div className="w-14 h-14 rounded-xl bg-tf-bg-hover flex items-center justify-center mx-auto mb-4">
            <Tag className="w-7 h-7 text-tf-text-muted" />
          </div>
          <h3 className="text-base font-semibold text-tf-text mb-1">No brands yet</h3>
          <p className="text-sm text-tf-text-muted mb-6 max-w-sm mx-auto">
            Add your first brand to start managing your store portfolio
          </p>
          <Button
            variant="primary"
            onClick={handleCreate}
            icon={<Plus className="w-4 h-4" />}
          >
            Add Brand
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2 rounded-lg border border-tf-border">
          <table className="min-w-full">
            <thead>
              <tr className="bg-tf-bg-muted border-b border-tf-border">
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  Brand
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  URL
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider hidden lg:table-cell">
                  Description
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider hidden xl:table-cell">
                  Entity ID
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider hidden md:table-cell">
                  Created
                </th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tf-border-sub">
              {brands.map((brand) => (
                <tr key={brand.id} className="bg-tf-bg-card hover:bg-tf-bg-muted transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {brand.logoUrl ? (
                        <img
                          src={brand.logoUrl}
                          alt={brand.name}
                          className="w-9 h-9 rounded-lg object-cover ring-1 ring-tf-border"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-tf-bg-hover flex items-center justify-center">
                          <Tag className="w-4 h-4 text-tf-text-muted" />
                        </div>
                      )}
                      <span className="font-medium text-tf-text">{brand.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {brand.url ? (
                      <a
                        href={brand.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline truncate max-w-[180px] block"
                      >
                        {brand.url}
                      </a>
                    ) : (
                      <span className="text-sm text-tf-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <span className="text-sm text-tf-text-sub line-clamp-2 max-w-[200px]">
                      {brand.description || '—'}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden xl:table-cell">
                    <code className="text-xs font-mono text-tf-text-muted bg-tf-bg-muted px-2 py-1 rounded">
                      {brand.agentplatEntityId ? `${brand.agentplatEntityId.slice(0, 12)}…` : '—'}
                    </code>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-sm text-tf-text-muted">
                    {new Date(brand.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      {brand.url && (
                        <button
                          onClick={() => handleViewEntity(brand)}
                          className="p-2 text-tf-text-muted hover:text-tf-text-sub hover:bg-tf-bg-hover rounded-lg transition-colors"
                          title="View company information"
                        >
                          <Building2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(brand)}
                        className="p-2 text-tf-text-muted hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(brand.id)}
                        className="p-2 text-tf-text-muted hover:text-red-600 hover:bg-tf-feedback-error-bg rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BrandModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        brand={editingBrand}
      />
    </div>
  )
})

MyBrandsTab.displayName = 'MyBrandsTab'

export default MyBrandsTab


